/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


// Utilities
function setCookie(cname, cvalue, exdays) {
    var d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    var expires = "expires="+d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

function deleteCookie(cname) {
    document.cookie = cname + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}

function getCookie(cname) {
    var name = cname + "=";
    var ca = document.cookie.split(';');
    for(var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

// PRODUCT SUBSCRIPTION ONETIME WIDGET
customElements.define(
    'product-subscription-widget',
    class ProductSubscriptionWidget extends HTMLElement {
        constructor() {
            super();

            this.selectors = {
                collapsibleTrigger: '[data-collapsible-trigger]',
                collapsibleContent: '[data-collapsible-content]',
                onetimeInput:       '[data-onetime-trigger]',
                subscriptionInput:  '[data-subscription-trigger]',
                subscriptionInfos:  '[data-subscription-infos]',
                subscriptionPlans:  '[data-subscription-plans]',
                subscriptionOnly:   '[data-subscription-only]',
            };

            this.collapsibleTrigger = this.querySelector(this.selectors.collapsibleTrigger);
            this.collapsibleContent = this.querySelector(this.selectors.collapsibleContent);
            this.onetimeInput = this.querySelector(this.selectors.onetimeInput);
            this.subscriptionInput = this.querySelector(this.selectors.subscriptionInput);
            this.subscriptionOnly = this.querySelector(this.selectors.subscriptionOnly);
            this.subscriptionInfos = this.querySelector(this.selectors.subscriptionInfos);
            this.subscriptionPlans = this.querySelector(this.selectors.subscriptionPlans);

            this.defaultFrequency = Number(this.getAttribute('data-default-frequency') - 1) || null

            this.init();


        }

        init() {

          const ORDER_TYPE_ONETIME = 'onetime';
          const ORDER_TYPE_SUBSCRIPTION = 'subscription';
          const blockSwatches = document.querySelectorAll('.block-swatch-list .block-swatch')
          const selectedSwatch = Array.from(blockSwatches).filter((swatch) => swatch.querySelector('.block-swatch__radio').checked)[0];

          const enableVariantForOrderType = (orderType) => {
              const orderAvailability = {
                onetime: "Achat unique",
                subscription: "Abonnement",
                both: "Achat unique et abonnement"
              };

                const options = document.querySelectorAll('div[data-order-availability]');
                if (options.length === 0) return;

                const availabilityMap = {
                    'onetime': [orderAvailability.onetime, orderAvailability.both],
                    'subscription': [orderAvailability.subscription, orderAvailability.both]
                };

                options.forEach(option => {
                    option.classList.toggle('hidden', !availabilityMap[orderType].includes(option.dataset.orderAvailability));
                });

                const firstOption = document.querySelector('div[data-order-availability]:not(.hidden)');
                firstOption?.querySelector('input')?.click();
            };

            const selectOrderType = (orderType) => {
                if (orderType === ORDER_TYPE_SUBSCRIPTION) {
                    this.subscriptionInput.checked = true;
                    this.subscriptionInfos.classList.remove('hidden');
                } else {
                    this.onetimeInput.checked = true;
                    this.subscriptionInfos.classList.add('hidden');
                }

                enableVariantForOrderType(orderType);

                if (orderType === ORDER_TYPE_SUBSCRIPTION) {
                    const defaultFrequency = document.querySelector(
                        '.product-frequency.is-default-frequency'
                    );

                    if (defaultFrequency) {
                        defaultFrequency.click();
                    }
                }
            };

            this.onetimeInput.addEventListener('change', () => selectOrderType(ORDER_TYPE_ONETIME));
            this.subscriptionInput.addEventListener('change', () => selectOrderType(ORDER_TYPE_SUBSCRIPTION));

            let initialOrderType = this.onetimeInput.checked ? ORDER_TYPE_ONETIME : ORDER_TYPE_SUBSCRIPTION;

            if (selectedSwatch) {
                initialOrderType = selectedSwatch.dataset.orderAvailability === 'Achat unique' ? ORDER_TYPE_ONETIME : ORDER_TYPE_SUBSCRIPTION;
            }

            if (this.subscriptionOnly != null) {
                initialOrderType = ORDER_TYPE_SUBSCRIPTION;
            }

            selectOrderType(initialOrderType);

            const self = this;

            const remoteAddToCartButtons = this.querySelectorAll('.js-remote-add-to-cart');

            remoteAddToCartButtons.forEach((button) => {
                button.addEventListener('click', (event) => {
                    const addToCartButton = document.querySelector('.product-form__buy-buttons #AddToCart');
                    addToCartButton.click();
                    const clickEvent = new MouseEvent('click');
                    addToCartButton.dispatchEvent(clickEvent);
                });
            });

            // custom select
            document.addEventListener("click", this.closeAllSelect);

            var x, i, j, l, ll, selElmnt, a, b, c;
            /*look for any elements with the class "custom-select":*/
            x = document.getElementsByClassName("product-subscription-widget-custom-select");
            l = x.length;
            for (i = 0; i < l; i++) {
                selElmnt = x[i].getElementsByTagName("select")[0];
                if (selElmnt.classList.contains('rendered')) {
                    return;
                }
                ll = selElmnt.length;

                /*for each element, create a new DIV that will act as the selected item:*/
                a = document.createElement("DIV");
                a.setAttribute("class", "product-subscription-widget-select-selected");
                a.innerHTML = selElmnt.options[selElmnt.selectedIndex].innerHTML.replaceAll('#span#', '<span class="product-subscription-widget-select-green">').replaceAll('#endspan#', '</span>').replaceAll('#brmobile#', '<br class="br-mobile">');

                // Create the SVG element
                function createSVG() {
                    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                    svg.setAttribute("focusable", "false");
                    svg.setAttribute("width", "12");
                    svg.setAttribute("height", "8");
                    svg.setAttribute("class", "icon icon--chevron");
                    svg.setAttribute("viewBox", "0 0 12 8");

                    var path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                    path.setAttribute("fill", "none");
                    path.setAttribute("d", "M1 1l5 5 5-5");
                    path.setAttribute("stroke", "currentColor");
                    path.setAttribute("stroke-width", "2");

                    svg.appendChild(path);
                    return svg;
                }

                // Append the SVG to the DIV
                a.appendChild(createSVG());

                x[i].appendChild(a);
                /*for each element, create a new DIV that will contain the option list:*/
                b = document.createElement("DIV");
                b.setAttribute("class", "product-subscription-widget-select-items product-subscription-widget-select-hide");
                for (j = 0; j < ll; j++) {
                    /*for each option in the original select element,
                    create a new DIV that will act as an option item:*/
                    c = document.createElement("DIV");
                    c.innerHTML = selElmnt.options[j].innerHTML.replaceAll('#span#', '<span class="product-subscription-widget-select-green">').replaceAll('#endspan#', '</span>').replaceAll('#brmobile#', '<br class="br-mobile">');
                    c.dataset.value = selElmnt.options[j].value;

                    if (j === this.defaultFrequency) {
                        c.classList.add('is-default-frequency');

                        const recommended = document.createElement('p');
                        recommended.className = 'bg-green-light text-white px-[10px] py-[5px] text-[12px] top-[-10px] left-1/2 -translate-x-1/2 rounded-[20px] leading-none absolute';
                        recommended.textContent = this.getAttribute('data-recommended');

                        c.appendChild(recommended);
                    }
                    c.classList.add('product-frequency', 'relative');

                    c.addEventListener("click", function(e) {
                        /*when an item is clicked, update the original select box,
                        and the selected item:*/
                        console.log(c)
                        var y, i, k, s, h, sl, yl;
                        s = this.parentNode.parentNode.getElementsByTagName("select")[0];
                        sl = s.length;
                        h = this.parentNode.previousSibling;
                        for (i = 0; i < sl; i++) {
                            if (s.options[i].value == this.dataset.value) {
                                s.selectedIndex = i;
                                h.innerHTML = this.innerHTML;

                                var existingSvg = h.querySelector('svg.icon.icon--chevron');
                                if (existingSvg) {
                                    existingSvg.remove();
                                }

                                h.appendChild(createSVG());

                                console.log(h)


                                y = this.parentNode.getElementsByClassName("product-subscription-widget-same-as-selected");
                                yl = y.length;
                                for (k = 0; k < yl; k++) {
                                    y[k].classList.remove("product-subscription-widget-same-as-selected");
                                }
                                this.classList.add('product-subscription-widget-same-as-selected', 'relative');

                                break;
                            }
                        }

                        if (this.closest('.product-sticky-buybox')) {
                            console.log('passe ici')
                            var stickySelected = this.closest('.product-sticky-buybox').querySelector('.product-subscription-widget-select-selected');
                            if (stickySelected) {
                                // Remove any existing SVG before adding a new one
                                var existingSvg = stickySelected.querySelector('svg.icon.icon--chevron');
                                if (existingSvg) {
                                    existingSvg.remove();
                                }

                                console.log(stickySelected)
                                // Append the SVG to the stickySelected DIV
                                stickySelected.appendChild(createSVG());
                            }
                        }

                        h.click();
                    });
                    b.appendChild(c);

                }
                x[i].appendChild(b);
                selElmnt.classList.add('rendered');
                a.addEventListener("click", function(e) {
                    /*when the select box is clicked, close any other select boxes,
                    and open/close the current select box:*/
                    e.stopPropagation();
                    self.closeAllSelect(this);
                    this.nextSibling.classList.toggle("product-subscription-widget-select-hide");
                    this.classList.toggle("product-subscription-widget-select-arrow-active");
                    var y, i, k, s, h, sl, yl;
                    s = this.parentNode.parentNode.getElementsByTagName("select")[0];
                    sl = this.nextSibling.childNodes.length;
                    h = this.parentNode.previousSibling;
                    for (i = 0; i < sl; i++) {
                        if (s.value == this.nextSibling.childNodes[i].dataset.value) {
                            this.nextSibling.childNodes[i].classlist.add( "product-subscription-widget-same-as-selected", "relative");
                            break;
                        }
                    }
                });
            }

        }

        insertAfter(referenceNode, newNode) {
            referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
        }

        getSellingPlan() {
            return this.subscriptionPlans.value;
        }

        closeAllSelect(elmnt) {
            /*a function that will close all select boxes in the document,
            except the current select box:*/
            var x, y, i, xl, yl, arrNo = [];
            x = document.getElementsByClassName("product-subscription-widget-select-items");
            y = document.getElementsByClassName("product-subscription-widget-select-selected");
            xl = x.length;
            yl = y.length;
            for (i = 0; i < yl; i++) {
                if (elmnt == y[i]) {
                    arrNo.push(i)
                } else {
                    y[i].classList.remove("product-subscription-widget-select-arrow-active");
                }
            }
            for (i = 0; i < xl; i++) {
                if (arrNo.indexOf(i)) {
                    x[i].classList.add("product-subscription-widget-select-hide");
                }
            }
        }
    },
);



// PRODUCT FEATURES
customElements.define(
    'product-features',
    class ProductFeatures extends HTMLElement {
        constructor() {
            super();

            let selectors = {
                productNumber : '[data-collapse-id]',
                productNumberData : 'collapseId',
                collapsibleTrigger : '[data-button-id]',
                collapsibleTriggerData : 'buttonId',
            };

            this.selectors = selectors;

            this.collapsibleTrigger = this.querySelectorAll(this.selectors.collapsibleTrigger);
            this.productNumber = this.querySelectorAll(this.selectors.productNumber);

            this.init();

        }

    init() {
      this.productNumber.forEach((number) => {
        number.addEventListener('click', (event) => {
          var id = event.target.dataset[this.selectors.productNumberData];
          this.collapsibleTrigger.forEach((button) => {
            if (button.dataset[this.selectors.collapsibleTriggerData] == id) {
              button.click();
            }
          });

        });
      });
    }
  },
);

// // CartFreeProductItem
class CartFreeProductItem extends HTMLElement {
  constructor() {
      super();
  }

  connectedCallback() {
    this.CartFreeProductItem = this;
    const isNoLongerEligible = this.CartFreeProductItem.hasAttribute('free-product-no-longer-eligible');
    const freeProductId = this.CartFreeProductItem.dataset.freeProductId

    if (isNoLongerEligible) {
      console.log('no eligible')
      this.updateQuantity(freeProductId, 0);
    }
    console.log('free product')
  }

  updateQuantity(id, quantity) {
    const body = JSON.stringify({
      "id": id,
      "quantity": quantity
    });

    console.log(body)

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: body
    })
    .then(response => {
      console.log(response)
      if (window.location.href.indexOf("/cart") > -1) {
        /*document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));*/

        //window.location.reload();
        if (response.ok) {
          window.location.reload();
        }
      } else {
        document.getElementById('mini-cart').open = false;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
        document.getElementById('mini-cart').open = true;
      }
    })
    .then(data => console.log(data));
  }
}

customElements.define('cart-free-item', CartFreeProductItem);


function addCloseEventListeners() {
    var productCrossSellClose = document.querySelectorAll('.product-item--close');

    productCrossSellClose.forEach((element) => {
        element.addEventListener('click', (event) => {

            var productId = event.currentTarget.getAttribute('product-id');
            var removeToClick = `.line-item__remove-button[product-id="${productId}"]`;
            var removeButton = document.querySelector(removeToClick);

            if (removeButton) {
                removeButton.click();
            }
        });
    });
}

const observerConfig = {
    childList: true,
    subtree: true
};

const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
            addCloseEventListeners();
        }
    });
});

const targetNode = document.body;
observer.observe(targetNode, observerConfig);

addCloseEventListeners();

document.addEventListener('DOMContentLoaded', function () {
    const importantProductIds = ['8523540562193', '8578650767633', '8578661777681', '8523534074129'];

    document.body.addEventListener('click', function (event) {
        const deleteButton = event.target.closest('.product-delete-recharge');
        if (!deleteButton) return;

        event.preventDefault();

        const targetProductId = deleteButton.getAttribute('product-id');

        // 1. Récupère le panier
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                // Compte le nombre de produits importants dans le panier
                const importantItems = cart.items.filter(item =>
                    importantProductIds.includes(String(item.product_id)) &&
                    item.selling_plan_allocation
                );

                console.log('important', importantItems)

                // Vérifie si celui qu'on s'apprête à supprimer est le dernier
                const isLastImportantProduct = (
                    importantItems.length === 1 &&
                    String(importantItems[0].product_id) === targetProductId
                );

                console.log(isLastImportantProduct)

                if (isLastImportantProduct) {
                    // 2. Supprimer tous les produits _crosssell
                    const itemsToRemove = cart.items.filter(item =>
                        item.properties &&
                        item.properties._crosssell === "true"
                    );

                    console.log(itemsToRemove)

                    const updates = {};
                    itemsToRemove.forEach(item => {
                        updates[item.key] = 0;
                    });

                    console.log(updates)

                    fetch('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ updates })
                    }).then(() => {
                        // Puis clic sur le bouton de suppression d'origine
                        const removeBtn = document.querySelector(
                            `.line-item__remove-button.delete-recharge[product-id="${targetProductId}"]`
                        );
                        if (removeBtn) {
                            removeBtn.click();
                        }
                    });

                } else {
                    const removeBtn = document.querySelector(
                        `.line-item__remove-button.delete-recharge[product-id="${targetProductId}"]`
                    );
                    if (removeBtn) {
                        removeBtn.click();
                    }
                }
            });
    });
});
